import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { auth, authReady, appCheckActive } from '../firebase.js';
import { ensureUserDoc } from '../util/playContract.js';

// Session lifecycle for play-first web.
//
// status:
//   'starting'      — establishing the anonymous Firebase session
//   'ready'         — signed in (anon or otherwise); play is available
//   'unavailable'   — anonymous sign-in itself failed (e.g. the provider isn't
//                     enabled in the Console). NOTE: an App Check activation
//                     failure does NOT flip this — it doesn't make
//                     signInAnonymously throw — so AppShell renders the wall off
//                     `appCheckActive` instead (audit B17).
//
// The users/{uid} doc is NOT created here. It's created lazily on the
// first answer via `claimUserDoc()` (which the screens await before any
// vote write), so bouncers don't seed founder groups. See playContract.js.

const SessionContext = createContext(null);

export function SessionProvider({ children }) {
  const [status, setStatus] = useState('starting');
  const [uid, setUid] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [providerId, setProviderId] = useState(null);
  const [saveAccountOpen, setSaveAccountOpen] = useState(false);
  // The account modal serves two intents: 'save' (link the anon session in
  // place, keep progress) and 'login' (sign IN to an existing saved account,
  // for recovery / a new device). Same modal, different verbs + auth calls.
  const [authIntent, setAuthIntent] = useState('save');
  // Optional context for the save modal: `{ reason, inviter }`. Lets a caller
  // reframe the ask (e.g. reason:'h2h_lockin' → "Lock in your matchup" + the
  // post-save install chain) without a new modal. Null = the default save copy.
  const [saveContext, setSaveContext] = useState(null);
  const userDocClaimed = useRef(false);
  // The single in-flight users/{uid}-doc create promise. Both the secured-user
  // ensure path and the first-vote claim path funnel through it, so a fast first
  // vote firing WHILE the secured ensure is still awaiting can't double-create
  // the doc (audit B5).
  const claimPromiseRef = useRef(null);
  // A guest-chosen display name to apply when the user doc is first created
  // (anonymous players have no doc yet — see the ensureUserDoc deferral).
  // Persisted to localStorage so a guest who renames then reloads before
  // saving/voting keeps the name (audit B10); rehydrated on mount below.
  const pendingNameRef = useRef(null);
  // Last uid seen from onAuthStateChanged — detects an account switch (a login
  // replacing the anon session) so we re-arm the doc-claim guard for the new uid.
  const lastUidRef = useRef(null);

  // Ensure the users/{uid} doc exists for a SECURED (non-anonymous) user.
  // Anonymous "bouncers" stay deferred (their doc is created on the first vote
  // via claimUserDoc) so drive-by visitors don't seed throwaway founder groups
  // — but a user who has SAVED/linked or LOGGED IN is a committed account, so
  // create it now. This is what makes username rename work (Profile does an
  // .update() that 404s on a missing doc), seeds their First Group
  // (onUserProfileCreated → createFounderGroupForUser, with them as
  // creatorUserId so they can rename it), and lets claimWebFirstSetBonus find a
  // doc to bank the +60 onto. Idempotent — ensureUserDoc no-ops if present.
  // Single-flight users/{uid}-doc create. Memoizes the in-flight promise so
  // concurrent callers (a secured-user ensure + a racing first vote) share ONE
  // create instead of both calling ensureUserDoc (audit B5). Clears the cached
  // promise on failure so a later attempt can retry, and clears the persisted
  // pending name once a doc exists (it has served its purpose) (audit B10).
  const ensureUserDocOnce = useCallback((uidArg, displayName) => {
    if (userDocClaimed.current) return Promise.resolve();
    if (!claimPromiseRef.current) {
      claimPromiseRef.current = ensureUserDoc(uidArg, displayName)
        .then(() => {
          userDocClaimed.current = true;
          try {
            localStorage.removeItem('ps_pending_name');
          } catch {
            /* storage unavailable — non-fatal */
          }
        })
        .catch((e) => {
          claimPromiseRef.current = null; // allow a retry on the next call
          throw e;
        });
    }
    return claimPromiseRef.current;
  }, []);

  const ensureSecuredUserDoc = useCallback(
    async (authUser) => {
      const u = authUser || auth.currentUser;
      if (!u || u.isAnonymous) return;
      await ensureUserDocOnce(
        u.uid,
        pendingNameRef.current || u.displayName || undefined
      );
    },
    [ensureUserDocOnce]
  );

  // Rehydrate a guest's pending display name from a prior session — without
  // this, a rename then reload before save/first-vote loses the name (B10).
  useEffect(() => {
    try {
      const n = localStorage.getItem('ps_pending_name');
      if (n) pendingNameRef.current = n;
    } catch {
      /* storage unavailable */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let signingIn = false;
    let unsub = () => {};

    // CRITICAL: drive the whole session off onAuthStateChanged and NEVER sign
    // in anonymously eagerly. On a refresh, Firebase restores the persisted
    // user (anon OR a saved/linked account) asynchronously — `auth.currentUser`
    // is null until that completes. The old code called signInAnonymously()
    // immediately, which during that restore window minted a FRESH anonymous
    // user that clobbered the persisted saved account (data loss — the user
    // could no longer reach their saved account). Now we only start an
    // anonymous session AFTER restoration reports "no user", so a saved account
    // survives every refresh.
    //
    // AND: wait for `authReady` (persistence pinned to LOCAL, or the SESSION
    // fallback) BEFORE attaching the listener — so the signInAnonymously() below
    // can't fire before persistence is set and land the session in in-memory
    // storage that dies on the next reload (the "had to log in again" bug).
    // authReady never rejects.
    authReady.then(() => {
      if (cancelled) return;
      unsub = auth.onAuthStateChanged(async (user) => {
      if (cancelled) return;
      if (user) {
        // Account switch (e.g. a login replaced the anon session with a
        // different uid) → re-arm the claim guard so the new account's doc
        // gets ensured.
        if (lastUidRef.current && lastUidRef.current !== user.uid) {
          userDocClaimed.current = false;
          claimPromiseRef.current = null;
        }
        lastUidRef.current = user.uid;
        setUid(user.uid);
        setIsAnonymous(!!user.isAnonymous);
        setProviderId(user.providerData?.[0]?.providerId ?? null);
        setStatus('ready');
        // Secured users get their doc + First Group now, not only on first
        // vote. Fire-and-forget so 'ready' isn't blocked; anon stays deferred.
        if (!user.isAnonymous) {
          ensureSecuredUserDoc(user).catch((e) =>
            console.error('[app-web] ensureSecuredUserDoc failed:', e)
          );
        }
        return;
      }
      // No persisted user (first-ever visit, or after an explicit sign-out) →
      // begin an anonymous session. onAuthStateChanged fires again with the new
      // user, taking the branch above. Guarded so a transient null can't kick
      // off two concurrent sign-ins.
      if (signingIn) return;
      signingIn = true;
      try {
        await auth.signInAnonymously();
      } catch (e) {
        if (cancelled) return;
        // auth/operation-not-allowed → anon provider not enabled in Console
        console.error('[app-web] anonymous sign-in failed:', e);
        setErrorCode(e?.code || 'unknown');
        setStatus('unavailable');
      } finally {
        signingIn = false;
      }
      });
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  // THE 'starting' WATCHDOG (2026-08-24, Tom: "I'm getting only shimmering
  // skeletons when I visit web play profile view and stories as a new user").
  //
  // 'starting' was the one session state with no floor under it. 'unavailable'
  // renders AppShell's wall and 'ready' renders the app, but 'starting' does
  // neither — the children mount and every read-gated screen sits on its own
  // skeleton branch (`status === 'starting' || …`). So anything that stops the
  // auth handshake from completing produced an app-wide, permanent shimmer
  // with no error, no wall, no retry and nothing in the UI to diagnose from.
  //
  // It is specifically a NEW-VISITOR failure, which is what makes it
  // expensive: a returning reader has a persisted user, so
  // `onAuthStateChanged` fires immediately and the app is fine. A first-ever
  // visitor must complete `signInAnonymously()`, and if that call never
  // settles — a blocked or slow reCAPTCHA mint, storage partitioning in a
  // private window, a captive network — the callback never fires a second
  // time and the status never moves. The web-first invite loop lands every
  // new user on exactly that path.
  //
  // The floor does not diagnose the cause; it just refuses to shimmer
  // forever. 12s is deliberately generous — a cold start on slow mobile with
  // an App Check mint can legitimately take several seconds, and a false
  // wall shown to someone who was 300ms from playing is its own bug.
  useEffect(() => {
    if (status !== 'starting') return undefined;
    const timer = setTimeout(() => {
      setStatus((s) => (s === 'starting' ? 'unavailable' : s));
      setErrorCode((c) => c || 'session_timeout');
    }, 12000);
    return () => clearTimeout(timer);
  }, [status]);

  // Create users/{uid} on first real interaction. Idempotent + memoized so
  // concurrent first taps don't double-create. MUST resolve before the
  // first writeAnswer (awardSubmissionPoints no-ops without the doc).
  const claimUserDoc = useCallback(
    async (displayName) => {
      if (!uid) throw new Error('no session');
      await ensureUserDocOnce(uid, pendingNameRef.current || displayName);
    },
    [uid, ensureUserDocOnce]
  );

  // Stash a guest-chosen display name (from the Profile rename editor) so an
  // anonymous player's chosen name survives into their account when the user
  // doc is first created (on save / first vote).
  const setPendingDisplayName = useCallback((name) => {
    const v = (name && name.trim()) || null;
    pendingNameRef.current = v;
    // Persist so the chosen name survives a reload before save / first vote (B10).
    try {
      if (v) localStorage.setItem('ps_pending_name', v);
      else localStorage.removeItem('ps_pending_name');
    } catch {
      /* storage unavailable — keep the in-memory value */
    }
  }, []);

  // Re-read identity after an account upgrade (linkWithCredential keeps the
  // same UID but flips isAnonymous → false + populates providerData).
  const refreshIdentity = useCallback(() => {
    const user = auth.currentUser;
    if (!user) return;
    setIsAnonymous(!!user.isAnonymous);
    setProviderId(user.providerData?.[0]?.providerId ?? null);
  }, []);

  const openSaveAccount = useCallback((ctx) => {
    // Guard: `onClick={openSaveAccount}` hands us a click event — only accept a
    // real context object (one carrying a `reason`); ignore anything else.
    setSaveContext(ctx && ctx.reason ? ctx : null);
    setAuthIntent('save');
    setSaveAccountOpen(true);
  }, []);
  const openLogin = useCallback(() => {
    setSaveContext(null);
    setAuthIntent('login');
    setSaveAccountOpen(true);
  }, []);
  const closeSaveAccount = useCallback(() => {
    setSaveAccountOpen(false);
    setSaveContext(null);
  }, []);

  const value = useMemo(
    () => ({
      status,
      uid,
      errorCode,
      appCheckActive,
      claimUserDoc,
      ensureSecuredUserDoc,
      setPendingDisplayName,
      isAnonymous,
      providerId,
      refreshIdentity,
      saveAccountOpen,
      authIntent,
      saveContext,
      openSaveAccount,
      openLogin,
      closeSaveAccount,
    }),
    [
      status,
      uid,
      errorCode,
      claimUserDoc,
      ensureSecuredUserDoc,
      setPendingDisplayName,
      isAnonymous,
      providerId,
      refreshIdentity,
      saveAccountOpen,
      authIntent,
      saveContext,
      openSaveAccount,
      openLogin,
      closeSaveAccount,
    ]
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}
